import React, { memo } from "react";
import Animated, {
  FadeInUp,
  FadeOutDown,
  ReduceMotion,
} from "react-native-reanimated";

import SkeletonPlaceholder from "@/components/molecules/SkeletonPlaceholder";

import { Box } from "@/components/atoms/Box";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";

const AnimatedView =
  (Animated as typeof Animated & { View: typeof Animated.View }).View || Box;

/**
 *
 */
interface Props {
  skeletonMetrics: (boolean[] | null)[][];
  skeletonMetricsChart: string[];
  isSmallScreen: boolean;
  isDark: boolean;
}

/**
 * Skeleton loading placeholder for player detail view with metrics
 * @param root0 - The component props
 * @param root0.skeletonMetrics - 2D array of boolean flags indicating which metric skeleton placeholders to show
 * @param root0.isSmallScreen - Whether device has small screen for responsive skeleton layout
 * @param root0.isDark - Whether dark theme is active for styling skeleton background colors
 */
const SkeletonPlayerDetailView = ({
  skeletonMetrics,
  isSmallScreen,
  isDark,
}: Props) => (
  <AnimatedView
    entering={FadeInUp.duration(500).reduceMotion(ReduceMotion.System)}
    exiting={FadeOutDown.duration(500).reduceMotion(ReduceMotion.System)}
  >
    <ScrollView>
      <SkeletonPlaceholder>
        <ScrollView
          contentContainerClassName={`gap-6 pt-10 ${!isSmallScreen ? "px-4" : ""}`}
        >
          <Stack direction="horizontal" className="items-center gap-6">
            <Box
              className={`h-14 flex-1 animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
            />
            <>
              <Box
                className={`size-12 animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
              />
              <Box
                className={`size-12 animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
              />
              <Box
                className={`size-12 animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
              />
            </>
          </Stack>
          <Box
            className={`h-10 w-full animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
          />
          <Box
            className={`h-8 w-1/2 animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
          />
          <Stack className="gap-4 pt-4">
            {skeletonMetrics.map((metric, i) => (
              <Stack
                direction="horizontal"
                key={`player_detail_${i.toString()}`}
                className="flex-1 justify-between gap-4"
              >
                {metric.map(
                  (item, idx) =>
                    item && (
                      <Box className="flex-1" key={`metric_${idx.toString()}`}>
                        <Stack
                          className={`flex-1 gap-1 rounded-none px-3 pb-3 pt-2 ${!isSmallScreen ? "border border-surface-muted" : "border-2 border-surface-muted"}`}
                        >
                          <Box
                            className={`h-8 w-full animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
                          />
                          <Box
                            className={`h-6 w-full animate-pulse rounded-none opacity-70 ${isDark ? "bg-surface-strong" : "bg-surface-muted"}`}
                          />
                        </Stack>
                      </Box>
                    )
                )}
              </Stack>
            ))}
          </Stack>
        </ScrollView>
      </SkeletonPlaceholder>
    </ScrollView>
  </AnimatedView>
);

SkeletonPlayerDetailView.displayName = "SkeletonPlayerDetailView";

export default memo(SkeletonPlayerDetailView);
